// Safe localStorage (contract D.6). Every access is wrapped: private windows,
// blocked site data and quota errors fall back to defaults, and the page
// renders normally without storage. `ds-theme` stays in App.js as it is.

const SAVED_KEY = 'ds-saved-searches-v1';
const SAVED_MAX = 30;
const RECENT_KEY = 'ds-recent-shows-v1';
const RECENT_MAX = 50;

const store = () => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch (e) {
    return null; // SecurityError when site data is blocked
  }
};

export const safeGet = (key, fallback) => {
  try {
    const s = store();
    if (!s) return fallback;
    const raw = s.getItem(key);
    if (raw == null) return fallback;
    const v = JSON.parse(raw);
    return v === undefined ? fallback : v;
  } catch (e) {
    return fallback;
  }
};

// true when written
export const safeSet = (key, value) => {
  try {
    const s = store();
    if (!s) return false;
    if (value === undefined) s.removeItem(key);
    else s.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    return false;
  }
};

const isStr = (v) => typeof v === 'string' && v.length > 0;

// ---- saved searches ------------------------------------------------------
// entry = { id, bandKey, label, token, savedAt }; `token` is the
// links.encodeFindToken string. Newest first, at most 30.

const validSaved = (e) => !!(e && isStr(e.id) && isStr(e.bandKey) && isStr(e.token) && typeof e.label === 'string');

export const getSavedSearches = () => {
  const v = safeGet(SAVED_KEY, []);
  return Array.isArray(v) ? v.filter(validSaved).slice(0, SAVED_MAX) : [];
};

const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

// Saves `entry` (id and savedAt are filled in when missing) at the top. The
// same band + token saved again replaces the older entry. Returns the new
// list, or null if the entry is invalid.
export const saveSearch = (entry) => {
  if (!entry) return null;
  const e = {
    id: isStr(entry.id) ? entry.id : newId(),
    bandKey: entry.bandKey,
    label: String(entry.label == null ? '' : entry.label).slice(0, 60),
    token: entry.token,
    savedAt: typeof entry.savedAt === 'number' ? entry.savedAt : Date.now(),
  };
  if (!validSaved(e)) return null;
  const rest = getSavedSearches().filter((x) => x.id !== e.id && !(x.bandKey === e.bandKey && x.token === e.token));
  const list = [e].concat(rest).slice(0, SAVED_MAX);
  safeSet(SAVED_KEY, list);
  return list;
};

// Returns the new list.
export const deleteSavedSearch = (id) => {
  const list = getSavedSearches().filter((x) => x.id !== id);
  safeSet(SAVED_KEY, list);
  return list;
};

// ---- recent sids ---------------------------------------------------------
// { [bandKey]: [sid, ...] } newest first, 50 per band. Used ONLY as Surprise
// Me's `exclude`. Never render it as a list, history or stats (the rejected
// "My Tour" feature, contract H).

const getRecentMap = () => {
  const v = safeGet(RECENT_KEY, {});
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
};

export const getRecentSids = (bandKey) => {
  const list = getRecentMap()[bandKey];
  return Array.isArray(list) ? list.filter(isStr).slice(0, RECENT_MAX) : [];
};

export const pushRecentSid = (bandKey, sid) => {
  if (!isStr(bandKey) || !isStr(sid)) return;
  const map = getRecentMap();
  const prev = Array.isArray(map[bandKey]) ? map[bandKey].filter(isStr) : [];
  if (prev[0] === sid) return; // already newest: skip the write
  map[bandKey] = [sid].concat(prev.filter((x) => x !== sid)).slice(0, RECENT_MAX);
  safeSet(RECENT_KEY, map);
};
